import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../api';
import DashboardLayout from '../../../Components/Dashboard/DashboardLayout';
import './AdminSuite.css';

const normalizeRole = user => {
  if (user?.role === 'admin' || user?.accountType === 'admin') return 'admin';
  return 'student';
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingUserId, setSavingUserId] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/users');
      setUsers(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const changeRole = async user => {
    const nextRole = normalizeRole(user) === 'admin' ? 'student' : 'admin';

    try {
      setSavingUserId(user.id);
      await api.patch(`/admin/users/${user.id}`, { role: nextRole });
      toast.success(`Role updated to ${nextRole}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update role');
    } finally {
      setSavingUserId(null);
    }
  };

  const toggleSuspension = async user => {
    try {
      setSavingUserId(user.id);
      await api.patch(`/admin/users/${user.id}`, { is_suspended: !user.is_suspended });
      toast.success(user.is_suspended ? 'User reactivated' : 'User suspended');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user status');
    } finally {
      setSavingUserId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="container py-4 admin-suite">
        <div className="admin-suite-header">
          <div>
            <h3 className="admin-suite-title">User Management</h3>
            <p className="admin-suite-subtitle">Manage admin/student roles and suspend or reactivate accounts.</p>
          </div>
          <span className="badge bg-light text-dark">Total: {users.length}</span>
        </div>

        {loading ? (
          <div className="admin-card p-4">Loading users...</div>
        ) : (
          <div className="admin-table-wrap admin-card admin-users-table-wrap">
            <div className="admin-table-head admin-users-table-head">
              <div>User</div>
              <div>Role</div>
              <div>State</div>
              <div>Actions</div>
            </div>
            {users.map(user => {
              const role = normalizeRole(user);
              const suspended = Boolean(user.is_suspended);

              return (
                <div key={user.id} className="admin-table-row admin-users-table-row">
                  <div>
                    <strong>{user.fullName || user.name || 'Unnamed User'}</strong>
                    <div className="text-muted small">{user.email}</div>
                  </div>
                  <div>
                    <span className={`admin-pill ${role === 'admin' ? 'published' : 'draft'}`}>{role}</span>
                  </div>
                  <div>
                    <span className={`admin-pill ${suspended ? 'archived' : 'published'}`}>
                      {suspended ? 'suspended' : 'active'}
                    </span>
                  </div>
                  <div className="admin-actions">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => changeRole(user)}
                      disabled={savingUserId === user.id}
                    >
                      Toggle Role
                    </button>
                    <button
                      className={`btn btn-sm ${suspended ? 'btn-success' : 'btn-outline-danger'}`}
                      onClick={() => toggleSuspension(user)}
                      disabled={savingUserId === user.id}
                    >
                      {suspended ? 'Reactivate' : 'Suspend'}
                    </button>
                  </div>
                </div>
              );
            })}
            {!users.length && <div className="p-3 text-muted">No users found.</div>}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminUsersPage;
